import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Input, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Spinner, Toast } from "reactstrap";
import { FaTrash, FaEdit, FaCopy, FaSearch } from "react-icons/fa";
import ImageGenerator from "../../components/ImageGenerator";
import AdminPageShell, { AdminSurface } from "../../components/Admin/AdminPageShell";
import { getApiUrl, getMediaUrl } from "../../utils/site";

export default function GeneratedImages() {
  const [images, setImages] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [modal, setModal] = useState(false);
  const [newPrompt, setNewPrompt] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [loading] = useState(false);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", color: "" });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchImages = async () => {
    const res = await axios.get(getApiUrl("/api/gallery"), { headers: getAuthHeaders() });
    setImages(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    await axios.delete(getApiUrl(`/api/generated-images/${id}`), { headers: getAuthHeaders() });
    fetchImages();
  };

  const handleEdit = (img) => {
    setEditId(img.id);
    setEditPrompt(img.prompt);
    setModal(true);
  };

  const handleUpdate = async () => {
    await axios.put(
      getApiUrl(`/api/generated-images/${editId}`),
      { prompt: editPrompt },
      { headers: getAuthHeaders() }
    );
    setModal(false);
    setEditId(null);
    setEditPrompt("");
    fetchImages();
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newPrompt || !newImage) return;
    const formData = new FormData();
    formData.append("prompt", newPrompt);
    formData.append("image", newImage);
    await axios.post(getApiUrl("/api/generated-images"), formData, {
      headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
    });
    setNewPrompt("");
    setNewImage(null);
    fetchImages();
  };

  const filteredImages = images.filter((img) =>
    String(img.prompt || "").toLowerCase().includes(search.toLowerCase())
  );

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setToast({ show: true, message: "Image URL copied.", color: "success" });
  };

  return (
    <AdminPageShell
      eyebrow="Creative AI assets"
      title="Generated Images"
      description="Create, review, search, and manage generated image assets from one cleaner visual workspace."
    >
      <AdminSurface title="Generator workspace" subtitle="Create new AI jewellery visuals">
        <ImageGenerator onGenerated={fetchImages} />

        <Form onSubmit={handleAdd} className="roha-admin-form-grid" style={{ marginTop: 20 }}>
          <div className="roha-admin-form-grid roha-admin-form-grid--two">
            <FormGroup>
              <Label>Prompt</Label>
              <Input
                value={newPrompt}
                onChange={(e) => setNewPrompt(e.target.value)}
                placeholder="Prompt for a new image"
              />
            </FormGroup>
            <FormGroup>
              <Label>Upload image</Label>
              <Input type="file" accept="image/*" onChange={(e) => setNewImage(e.target.files[0])} />
            </FormGroup>
          </div>
          <Button color="primary" type="submit" disabled={loading} style={{ width: "fit-content" }}>
            Add image
          </Button>
        </Form>
      </AdminSurface>

      <AdminSurface
        title="Image library"
        subtitle={`${filteredImages.length} generated visuals`}
        actions={
          <div className="roha-admin-filterbar" style={{ marginBottom: 0 }}>
            <Input
              placeholder="Search by prompt..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button color="primary" onClick={fetchImages}>
              <FaSearch /> Search
            </Button>
          </div>
        }
      >
        {loading ? (
          <div className="roha-admin-empty">
            <Spinner color="info" /> Loading images...
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="roha-admin-empty">No images found.</div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 24,
            }}
          >
            {filteredImages.map((img) => (
              <div
                key={img.id}
                style={{
                  background: "#fff",
                  borderRadius: 24,
                  boxShadow: "0 18px 36px rgba(22,53,48,0.08)",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  border: "1px solid rgba(20, 51, 47, 0.08)",
                }}
              >
                <img
                  src={getMediaUrl(img.image_url)}
                  alt={img.prompt}
                  onClick={() => {
                    setSelectedImage(img);
                    setModalOpen(true);
                  }}
                  style={{ width: "100%", maxWidth: 220, height: 170, objectFit: "cover", borderRadius: 16, marginBottom: 12 }}
                />
                <div className="roha-admin-toolbar" style={{ marginBottom: 8 }}>
                  <Button size="sm" color="info" onClick={() => copyUrl(getMediaUrl(img.image_url))}>
                    <FaCopy />
                  </Button>
                  <Button size="sm" color="warning" onClick={() => handleEdit(img)}>
                    <FaEdit />
                  </Button>
                  <Button size="sm" color="danger" onClick={() => handleDelete(img.id)}>
                    <FaTrash />
                  </Button>
                </div>
                <div style={{ fontWeight: 600, color: "#22304a", textAlign: "center", marginBottom: 4 }}>
                  {img.prompt}
                </div>
                <div className="roha-admin-muted" style={{ fontSize: 12 }}>
                  {new Date(img.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </AdminSurface>

      <Modal isOpen={modal} toggle={() => setModal(!modal)}>
        <ModalHeader toggle={() => setModal(!modal)}>Edit image prompt</ModalHeader>
        <ModalBody>
          <Input value={editPrompt} onChange={(e) => setEditPrompt(e.target.value)} />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleUpdate}>
            Save
          </Button>
          <Button color="secondary" onClick={() => setModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered size="lg">
        <div style={{ padding: 24, textAlign: "center" }}>
          <img
            src={selectedImage ? getMediaUrl(selectedImage.image_url) : ""}
            alt=""
            style={{ width: "100%", maxWidth: 540, borderRadius: 16, marginBottom: 16 }}
          />
          <div style={{ fontWeight: 600, fontSize: 18, color: "#22304a", marginBottom: 8 }}>
            {selectedImage?.prompt}
          </div>
          <div className="roha-admin-muted">
            {selectedImage && new Date(selectedImage.created_at).toLocaleString()}
          </div>
        </div>
      </Modal>

      {toast.show && (
        <Toast isOpen={toast.show} color={toast.color} style={{ position: "fixed", top: 20, right: 20, zIndex: 9999 }}>
          <div className="toast-body">{toast.message}</div>
        </Toast>
      )}
    </AdminPageShell>
  );
}
